import React, { useState } from 'react';
import { ShieldCheck, CheckSquare, Cookie, ExternalLink, X } from 'lucide-react';
import type { BlockRenderProps } from './types';

export const LegalAgreementBlockRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const title = (node.props?.title as string) || 'Terms & Policies Agreement';
  const agreementText = (node.props?.agreementText as string) || 'By creating an account or enrolling, you agree to our policies:';
  const termsUrl = (node.props?.termsUrl as string) || '/terms-and-conditions';
  const privacyUrl = (node.props?.privacyUrl as string) || '/privacy-policy';
  const cookieUrl = (node.props?.cookieUrl as string) || '/cookie-policy';
  const requireMarketingConsent = !!node.props?.requireMarketingConsent;
  const marketingConsentText = (node.props?.marketingConsentText as string) || 'I agree to receive SMS, WhatsApp, and email updates regarding classes and exam schedules.';

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeCookie, setAgreeCookie] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4 text-xs text-foreground space-y-3">
      <div className="flex items-center gap-2 font-bold text-sm text-foreground">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <span>{title}</span>
      </div>

      <p className="text-muted-foreground">{agreementText}</p>

      <div className="space-y-2 pt-1 border-t border-border/60">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
          />
          <span className="leading-snug">
            I have read and agree to the{' '}
            <a href={termsUrl} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline inline-flex items-center gap-0.5">
              <span>Terms &amp; Conditions</span>
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreePrivacy}
            onChange={(e) => setAgreePrivacy(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
          />
          <span className="leading-snug">
            I agree to the collection and handling of my data as per the{' '}
            <a href={privacyUrl} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline inline-flex items-center gap-0.5">
              <span>Privacy Policy</span>
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreeCookie}
            onChange={(e) => setAgreeCookie(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
          />
          <span className="leading-snug">
            I accept the{' '}
            <a href={cookieUrl} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline inline-flex items-center gap-0.5">
              <span>Cookie Policy</span>
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </span>
        </label>

        {requireMarketingConsent && (
          <label className="flex items-start gap-2.5 cursor-pointer pt-1 border-t border-border/40">
            <input
              type="checkbox"
              checked={agreeMarketing}
              onChange={(e) => setAgreeMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
            />
            <span className="leading-snug text-muted-foreground">{marketingConsentText}</span>
          </label>
        )}
      </div>
    </div>
  );
};

export const ConsentCheckboxBlockRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const label = (node.props?.label as string) || 'I agree to the Terms of Service and Privacy Policy';
  const policyLink = (node.props?.policyLink as string) || '/privacy-policy';
  const [checked, setChecked] = useState(false);

  return (
    <div className="py-1">
      <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
        />
        <span>
          {label}{' '}
          <a href={policyLink} target="_blank" rel="noreferrer" className="text-primary underline ml-1">
            Review Policy
          </a>
        </span>
      </label>
    </div>
  );
};

export const CookieBannerBlockRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const noticeText = (node.props?.noticeText as string) || 'We use cookies to improve your learning experience, remember preferences, and analyze site traffic.';
  const acceptButtonText = (node.props?.acceptButtonText as string) || 'Accept All';
  const declineButtonText = (node.props?.declineButtonText as string) || 'Necessary Only';
  const cookiePolicyUrl = (node.props?.cookiePolicyUrl as string) || '/cookie-policy';
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return (
      <div className="p-2 border border-dashed border-border rounded text-[10px] text-muted-foreground text-center">
        Cookie Banner (Dismissed in preview)
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card text-card-foreground p-4 shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 my-2">
      <div className="flex items-start gap-3">
        <Cookie className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-xs leading-relaxed text-muted-foreground">
          <span>{noticeText}</span>{' '}
          <a href={cookiePolicyUrl} target="_blank" rel="noreferrer" className="text-primary font-semibold underline inline-flex items-center gap-0.5">
            <span>Read Cookie Policy</span>
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted text-foreground transition-colors"
        >
          {declineButtonText}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
        >
          {acceptButtonText}
        </button>
      </div>
    </div>
  );
};
